import { auth, db } from '../firebase/config';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { uploadImage } from './imageService';
import { normalizeCategoryId } from '../constants/categories';
import { createPost, normalizePost } from './postService';
import { getEffectiveLimits } from '../constants/limits';

export const normalizeSeries = (s) => {
  if (!s) return null;
  return {
    id: s.id,
    title: s.title || '',
    description: s.description || '',
    coverUrl: s.coverUrl || null,
    ownerId: s.ownerId || '',
    ownerName: s.ownerName || s.ownerUsername || 'Usuario',
    ownerPhotoURL: s.ownerPhotoURL || s.ownerProfileImage || null,
    categoryId: normalizeCategoryId(s.categoryId) || normalizeCategoryId(s.category) || null,
    followersCount: s.followersCount || 0,
    episodesCount: s.episodesCount || 0,
    lastEpisodeAt: s.lastEpisodeAt || null,
    createdAt: s.createdAt || null,
    updatedAt: s.updatedAt || null
  };
};

export async function createSeries({ title, description = '', categoryId = 'general', coverFile = null }) {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };

    const t = (title || '').trim();
    if (!t) return { success: false, error: 'Título requerido' };

    // Datos del usuario
    let ownerName = currentUser.displayName || 'Usuario';
    let ownerPhotoURL = currentUser.photoURL || null;
    let isPremium = false;
    try {
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      if (userSnap.exists()) {
        const u = userSnap.data();
        ownerName = u.displayName || u.username || ownerName;
        ownerPhotoURL = u.photoURL || u.profileImage || ownerPhotoURL;
        isPremium = u?.isPremium === true;
      }
    } catch {
      // ignore
    }

    // Límite de series (enforce real)
    const limits = getEffectiveLimits(isPremium);
    const seriesLimit = limits.series;
    if (!isPremium) {
      const seriesRef = collection(db, 'series');
      const qMine = query(seriesRef, where('ownerId', '==', currentUser.uid), limit(seriesLimit + 1));
      const snap = await getDocs(qMine);
      if (snap.size >= seriesLimit) {
        return { success: false, error: `Límite alcanzado: en cuenta gratuita puedes crear hasta ${seriesLimit} series.` };
      }
    }

    let coverUrl = null;
    if (coverFile) {
      const up = await uploadImage(coverFile, `series/${currentUser.uid}`, currentUser.uid);
      if (!up.success) return { success: false, error: up.error || 'Error al subir portada' };
      coverUrl = up.url;
    }

    const seriesDoc = {
      title: t,
      description: (description || '').trim(),
      coverUrl,
      ownerId: currentUser.uid,
      ownerName,
      ownerPhotoURL,
      categoryId: normalizeCategoryId(categoryId) || 'general',
      followersCount: 0,
      episodesCount: 0,
      lastEpisodeAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const ref = await addDoc(collection(db, 'series'), seriesDoc);
    return { success: true, seriesId: ref.id, series: normalizeSeries({ id: ref.id, ...seriesDoc }) };
  } catch (e) {
    console.error('createSeries error:', e);
    return { success: false, error: e.message || 'Error al crear serie' };
  }
}

export async function updateSeries(seriesId, updates) {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };
    if (!seriesId) return { success: false, error: 'Serie inválida' };

    const ref = doc(db, 'series', seriesId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { success: false, error: 'Serie no encontrada' };
    const data = snap.data();
    if (data.ownerId !== currentUser.uid) return { success: false, error: 'No autorizado' };

    const safeUpdates = { ...updates, updatedAt: serverTimestamp() };
    if (safeUpdates.categoryId) safeUpdates.categoryId = normalizeCategoryId(safeUpdates.categoryId);
    await updateDoc(ref, safeUpdates);
    return { success: true };
  } catch (e) {
    console.error('updateSeries error:', e);
    return { success: false, error: e.message || 'Error al actualizar serie' };
  }
}

export async function getSeriesById(seriesId) {
  try {
    const snap = await getDoc(doc(db, 'series', seriesId));
    if (!snap.exists()) return { success: false, error: 'Serie no encontrada' };
    return { success: true, series: normalizeSeries({ id: snap.id, ...snap.data() }) };
  } catch (e) {
    console.error('getSeriesById error:', e);
    return { success: false, error: e.message || 'Error al cargar serie' };
  }
}

export async function getNewSeries({ limitCount = 20, categoryId = null } = {}) {
  try {
    const seriesRef = collection(db, 'series');
    let q = query(seriesRef, orderBy('createdAt', 'desc'), limit(limitCount));
    const normalizedCategory = categoryId ? normalizeCategoryId(categoryId) : null;
    if (normalizedCategory && normalizedCategory !== 'all') {
      q = query(seriesRef, where('categoryId', '==', normalizedCategory), orderBy('createdAt', 'desc'), limit(limitCount));
    }
    const snap = await getDocs(q);
    const items = snap.docs.map((d) => normalizeSeries({ id: d.id, ...d.data() }));
    return { success: true, series: items };
  } catch (e) {
    console.error('getNewSeries error:', e);
    return { success: false, error: e.message || 'Error al cargar series' };
  }
}

export async function getTrendingSeries({ limitCount = 20, categoryId = null } = {}) {
  try {
    const seriesRef = collection(db, 'series');
    const normalizedCategory = categoryId ? normalizeCategoryId(categoryId) : null;

    // Preferir: lastEpisodeAt desc + followersCount desc (requiere índice)
    let q;
    if (normalizedCategory && normalizedCategory !== 'all') {
      q = query(
        seriesRef,
        where('categoryId', '==', normalizedCategory),
        orderBy('lastEpisodeAt', 'desc'),
        orderBy('followersCount', 'desc'),
        limit(limitCount)
      );
    } else {
      q = query(seriesRef, orderBy('lastEpisodeAt', 'desc'), orderBy('followersCount', 'desc'), limit(limitCount));
    }

    const snap = await getDocs(q);
    const items = snap.docs.map((d) => normalizeSeries({ id: d.id, ...d.data() }));
    return { success: true, series: items };
  } catch (e) {
    console.warn('getTrendingSeries primary query failed, fallback:', e);
    try {
      const seriesRef = collection(db, 'series');
      const normalizedCategory = categoryId ? normalizeCategoryId(categoryId) : null;
      let q2 = query(seriesRef, orderBy('followersCount', 'desc'), limit(limitCount));
      if (normalizedCategory && normalizedCategory !== 'all') {
        q2 = query(seriesRef, where('categoryId', '==', normalizedCategory), orderBy('followersCount', 'desc'), limit(limitCount));
      }
      const snap2 = await getDocs(q2);
      const items2 = snap2.docs.map((d) => normalizeSeries({ id: d.id, ...d.data() }));
      return { success: true, series: items2 };
    } catch (err) {
      console.error('getTrendingSeries fallback error:', err);
      return { success: false, error: err.message || 'Error al cargar series' };
    }
  }
}

export async function getSeriesCoverUrls(limitCount = 15) {
  try {
    const seriesRef = collection(db, 'series');
    // Preferir series con actividad reciente
    let q = query(seriesRef, orderBy('lastEpisodeAt', 'desc'), limit(Math.max(limitCount, 30)));
    let snap;
    try {
      snap = await getDocs(q);
    } catch {
      // fallback a recientes si lastEpisodeAt no existe aún
      q = query(seriesRef, orderBy('createdAt', 'desc'), limit(Math.max(limitCount, 30)));
      snap = await getDocs(q);
    }

    const urls = snap.docs
      .map((d) => d.data()?.coverUrl || null)
      .filter(Boolean);

    // unique
    const unique = Array.from(new Set(urls));
    return { success: true, urls: unique.slice(0, limitCount) };
  } catch (e) {
    console.warn('getSeriesCoverUrls error:', e);
    return { success: false, urls: [] };
  }
}

export async function getUserSeries(ownerId, limitCount = 50) {
  try {
    if (!ownerId) return { success: true, series: [] };
    const seriesRef = collection(db, 'series');
    const q = query(seriesRef, where('ownerId', '==', ownerId), orderBy('updatedAt', 'desc'), limit(limitCount));
    const snap = await getDocs(q);
    return { success: true, series: snap.docs.map((d) => normalizeSeries({ id: d.id, ...d.data() })) };
  } catch (e) {
    console.error('getUserSeries error:', e);
    return { success: false, error: e.message || 'Error al cargar series', series: [] };
  }
}

export async function getSeriesEpisodes(seriesId, limitCount = 50) {
  try {
    if (!seriesId) return { success: true, episodes: [] };
    const postsRef = collection(db, 'posts');
    const q = query(
      postsRef,
      where('seriesId', '==', seriesId),
      where('postType', '==', 'episode'),
      orderBy('episodeNumber', 'asc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    const posts = snap.docs.map((d) => normalizePost({ id: d.id, ...d.data() }));
    return { success: true, episodes: posts };
  } catch (e) {
    console.error('getSeriesEpisodes error:', e);
    return { success: false, error: e.message || 'Error al cargar episodios', episodes: [] };
  }
}

export async function listEpisodes(seriesId) {
  try {
    const epsRef = collection(db, 'series', seriesId, 'episodes');
    const q = query(epsRef, orderBy('episodeNumber', 'asc'));
    const snap = await getDocs(q);
    return {
      success: true,
      episodes: snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    };
  } catch (e) {
    console.error('listEpisodes error:', e);
    return { success: false, error: e.message || 'Error al cargar episodios', episodes: [] };
  }
}

async function getNextEpisodeNumber(seriesId) {
  const epsRef = collection(db, 'series', seriesId, 'episodes');
  const q = query(epsRef, orderBy('episodeNumber', 'desc'), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return 1;
  const max = snap.docs[0].data()?.episodeNumber || 0;
  return Number(max) + 1;
}

export async function createEpisodeWithMedia({ seriesId, title = '', caption = '', files = [] }) {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };
    if (!seriesId) return { success: false, error: 'Serie inválida' };
    if (!files || files.length === 0) return { success: false, error: 'Añade al menos una imagen' };

    const seriesSnap = await getDoc(doc(db, 'series', seriesId));
    if (!seriesSnap.exists()) return { success: false, error: 'Serie no encontrada' };
    const series = seriesSnap.data();
    if (series.ownerId && series.ownerId !== currentUser.uid) {
      return { success: false, error: 'Solo el creador de la serie puede agregar episodios' };
    }

    const episodeNumber = await getNextEpisodeNumber(seriesId);

    // Datos del usuario
    let userDisplayName = currentUser.displayName || 'Usuario';
    let userPhotoURL = currentUser.photoURL || null;
    let username = null;
    let isPremium = false;
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        userDisplayName = userData.displayName || userData.username || userDisplayName;
        userPhotoURL = userData.photoURL || userData.profileImage || userPhotoURL;
        username = userData.username || null;
        isPremium = userData?.isPremium === true;
      }
    } catch {
      // ignore
    }

    // Límite de episodios por serie (enforce real)
    const limits = getEffectiveLimits(isPremium);
    const episodesLimit = limits.episodesPerSeries;
    if (!isPremium) {
      const currentEpisodesCount = Number(series?.episodesCount ?? NaN);
      if (Number.isFinite(currentEpisodesCount) && currentEpisodesCount >= episodesLimit) {
        return {
          success: false,
          error: `Límite alcanzado: en cuenta gratuita puedes publicar hasta ${episodesLimit} episodios por serie.`
        };
      }

      // Fallback: contar subcolección episodes si episodesCount no existe o no es confiable
      if (!Number.isFinite(currentEpisodesCount)) {
        try {
          const epsRef = collection(db, 'series', seriesId, 'episodes');
          const qCount = query(epsRef, limit(episodesLimit + 1));
          const snap = await getDocs(qCount);
          if (snap.size >= episodesLimit) {
            return {
              success: false,
              error: `Límite alcanzado: en cuenta gratuita puedes publicar hasta ${episodesLimit} episodios por serie.`
            };
          }
        } catch {
          // ignore (si falla, seguimos; el guardrail fuerte sería en backend)
        }
      }
    }

    // Subir páginas
    const media = [];
    const mediaUrls = [];
    for (const file of files) {
      const up = await uploadImage(file, `episodes/${currentUser.uid}/${seriesId}`, currentUser.uid);
      if (!up.success) return { success: false, error: up.error || 'Error al subir imagen' };
      media.push({ type: 'image', url: up.url, thumbnailUrl: up.url });
      mediaUrls.push({ type: 'image', url: up.url, thumbnailUrl: up.url });
    }

    const postData = {
      userId: currentUser.uid,
      userName: userDisplayName,
      userPhotoURL,
      caption: (caption || title || '').trim(),
      text: (caption || title || '').trim(),
      media,
      mediaUrls,
      category: series.categoryId || 'general',
      postType: 'episode',
      seriesId,
      episodeNumber,
      authorInfo: {
        uid: currentUser.uid,
        displayName: userDisplayName,
        username,
        profileImage: userPhotoURL
      }
    };

    // Crear post
    const createRes = await createPost(postData);
    if (!createRes.success) return createRes;

    const episodeRef = doc(db, 'series', seriesId, 'episodes', createRes.postId);
    await setDoc(episodeRef, {
      postId: createRes.postId,
      episodeNumber,
      title: (title || '').trim() || null,
      createdAt: serverTimestamp()
    });

    await updateDoc(doc(db, 'series', seriesId), {
      episodesCount: increment(1),
      lastEpisodeAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    const now = new Date();
    const postForFeed = normalizePost({
      id: createRes.postId,
      ...postData,
      likesCount: 0,
      commentsCount: 0,
      sharesCount: 0,
      createdAt: now,
      updatedAt: now
    });

    return { success: true, postId: createRes.postId, post: postForFeed, episodeNumber };
  } catch (e) {
    console.error('createEpisodeWithMedia error:', e);
    return { success: false, error: e.message || 'Error al crear episodio' };
  }
}

export async function followSeries(seriesId) {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };
    if (!seriesId) return { success: false, error: 'Serie inválida' };

    const seriesRef = doc(db, 'series', seriesId);
    const followerRef = doc(db, 'series', seriesId, 'followers', currentUser.uid);

    await runTransaction(db, async (tx) => {
      const s = await tx.get(seriesRef);
      if (!s.exists()) throw new Error('Serie no encontrada');

      const f = await tx.get(followerRef);
      if (f.exists()) return;

      tx.set(followerRef, { userId: currentUser.uid, createdAt: serverTimestamp() });
      tx.update(seriesRef, { followersCount: increment(1), updatedAt: serverTimestamp() });
    });

    return { success: true };
  } catch (e) {
    console.error('followSeries error:', e);
    return { success: false, error: e.message || 'Error al seguir serie' };
  }
}

export async function unfollowSeries(seriesId) {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };
    if (!seriesId) return { success: false, error: 'Serie inválida' };

    const seriesRef = doc(db, 'series', seriesId);
    const followerRef = doc(db, 'series', seriesId, 'followers', currentUser.uid);

    await runTransaction(db, async (tx) => {
      const s = await tx.get(seriesRef);
      if (!s.exists()) throw new Error('Serie no encontrada');

      const f = await tx.get(followerRef);
      if (!f.exists()) return;

      tx.delete(followerRef);
      tx.update(seriesRef, { followersCount: increment(-1), updatedAt: serverTimestamp() });
    });

    return { success: true };
  } catch (e) {
    console.error('unfollowSeries error:', e);
    return { success: false, error: e.message || 'Error al dejar de seguir serie' };
  }
}

export async function deleteSeries(seriesId) {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };
    const ref = doc(db, 'series', seriesId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { success: false, error: 'Serie no encontrada' };
    if (snap.data().ownerId !== currentUser.uid) return { success: false, error: 'No autorizado' };
    await deleteDoc(ref);
    return { success: true };
  } catch (e) {
    console.error('deleteSeries error:', e);
    return { success: false, error: e.message || 'Error al borrar serie' };
  }
}

